import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { client } from '../api/client';
import { Container } from '../components/ui/Container';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input as StyledInput, InputWrapper, Label } from '../components/ui/Input';
import { getApiErrorMessage } from '../utils/apiErrors';

const PageWrapper = styled.div`
  padding: 8rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  background: ${({ theme }) => theme.colors.primary.navy};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.primary.navy};
`;

const FormError = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.9rem;
  text-align: center;
`;

export const LoginPage = () => {
  const [step, setStep] = useState<'name' | 'passcode' | 'setup'>('name');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    passcode: '',
    confirmPasscode: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleCheckUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const res = await client.post('/auth/check', {
        firstName: formData.firstName,
        lastName: formData.lastName,
      });

      if (res.data.isActivated) {
        setStep('passcode');
      } else {
        setStep('setup');
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Gebruiker niet gevonden'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const res = await client.post('/auth/login', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        passcode: formData.passcode,
      });
      login(res.data.token, res.data.user);
      navigate(res.data.user.isAdmin ? '/admin' : '/member');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Aanmelden mislukt'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.passcode !== formData.confirmPasscode) {
      setError('Codes komen niet overeen');
      return;
    }
    if (formData.passcode.length !== 6) {
      setError('Code moet 6 cijfers zijn');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const res = await client.post('/auth/setup', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        passcode: formData.passcode,
      });
      login(res.data.token, res.data.user);
      navigate(res.data.user.isAdmin ? '/admin' : '/member');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Setup mislukt'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageWrapper>
      <Container>
        <Card style={{ maxWidth: '400px', margin: '0 auto', padding: '2rem', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
          <Title>
            {step === 'name' && 'Aanmelden'}
            {step === 'passcode' && `Welkom ${formData.firstName}`}
            {step === 'setup' && 'Activeer Account'}
          </Title>

          {step === 'name' && (
            <Form onSubmit={handleCheckUser}>
              <InputWrapper>
                <Label>Voornaam</Label>
                <StyledInput
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                />
              </InputWrapper>
              <InputWrapper>
                <Label>Achternaam</Label>
                <StyledInput
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  required
                />
              </InputWrapper>
              {error && <FormError>{error}</FormError>}
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Momentje...' : 'Verder'}
              </Button>
            </Form>
          )}

          {step === 'passcode' && (
            <Form onSubmit={handleLogin}>
               <InputWrapper>
                <Label>6-Cijferige Code</Label>
                <StyledInput
                  type="password"
                  maxLength={6}
                  value={formData.passcode}
                  onChange={(e) => setFormData({ ...formData, passcode: e.target.value })}
                  required
                />
              </InputWrapper>
              {error && <FormError>{error}</FormError>}
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Aanmelden...' : 'Aanmelden'}
              </Button>
            </Form>
          )}

          {step === 'setup' && (
            <Form onSubmit={handleSetup}>
              <p style={{ textAlign: 'center', marginBottom: '1rem' }}>
                Kies een persoonlijke 6-cijferige code.
              </p>
               <InputWrapper>
                <Label>Kies Code</Label>
                <StyledInput
                  type="password"
                  maxLength={6}
                  value={formData.passcode}
                  onChange={(e) => setFormData({ ...formData, passcode: e.target.value })}
                  required
                />
              </InputWrapper>
               <InputWrapper>
                <Label>Bevestig Code</Label>
                <StyledInput
                  type="password"
                  maxLength={6}
                  value={formData.confirmPasscode}
                  onChange={(e) => setFormData({ ...formData, confirmPasscode: e.target.value })}
                  required
                />
              </InputWrapper>
              {error && <FormError>{error}</FormError>}
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Activeren...' : 'Activeren'}
              </Button>
            </Form>
          )}
        </Card>
      </Container>
    </PageWrapper>
  );
};
